import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { useEffect, useMemo, useRef, useState } from "react";
import RiveAnimation from "../Common/RiveAnimation/RiveAnimation";
import { LoginContent } from "../LoginContent/LoginContent";
import { useLocation, useRoute } from "wouter";
import { cn } from "@/utils/styles";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";

export type RequiresLoginModalProps = {
    isOpen: boolean;
    mode?: string;
    onClose: () => void;
};

export const RequiresLoginModal: React.FC<RequiresLoginModalProps> = ({
    isOpen,
    mode = 'default',
    onClose
}) => {
    const [isSignUp, setIsSignUp] = useState(false);
    const [location] = useLocation();
    const locationRef = useRef(location);
    const [cannotClose, pageParams] = useRoute("/login/:section?");

    const title = useMemo(() => {
        return {
            default: (isSignUp ? 'Sign up for an account' : 'Log into your account'),
            createCluster: (isSignUp ? <>Sign up to Start<br />Your Deployment</> : <>Login to Start<br />Your Deployment</>)
        }[mode];
    }, [mode, isSignUp]);
    const description = useMemo(() => {
        return {
            default: "You're just one step away",
            createCluster: "You're just one step away"
        }[mode];
    }, [mode]);

    useEffect(() => {
        setIsSignUp(pageParams?.section === 'signup');
    }, [pageParams?.section]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setIsSignUp(mode !== 'default');

        const section = locationRef.current.split('/')[1];

        trackEvent(AnalyticsEventType.SIGNUP_CLICKED, {
            screen: section
        });
    }, [isOpen, mode, locationRef]);

    return (
        <Dialog open={isOpen} defaultOpen={isOpen} modal={!cannotClose}>
            <DialogContent
                // bg-[url('/assets/images/bg-login.png')]
                className={cn("flex flex-col custom-scrollbar max-h-[100vh] lg:min-h-[510px] max-md:max-w-[420px] w-[920px] max-w-full gap-0 overflow-y-auto !px-0 py-0 bg-black max-md:bg-none bg-no-repeat bg-right-bottom bg-[length:927px_659px]", cannotClose && "!z-[1]")}
                showCloseIcon={false}
                onClose={onClose}
            >
                <div className="absolute bottom-0 w-full h-[600px] overflow-hidden max-md:hidden">
                    <RiveAnimation
                        thumbnail="/assets/video/io.net.png"
                        src="/assets/video/io.net.riv"
                        width={918}
                        height={600}
                        className="absolute left-[240px] top-[-10px] w-[918px] h-[600px]"
                    />
                </div>


                <div className="flex flex-col gap-7 relative z-[1] flex-1">
                    <DialogHeader className="gap-3 p-7 pb-0">
                        <DialogTitle className="flex flex-col gap-3">
                            <div className="text-5xl font-medium text-white">{title}</div>

                            <div className="text-base text-gray-dark-2200">
                                {description}
                            </div>
                        </DialogTitle>
                    </DialogHeader>

                    <LoginContent isOpen={isOpen} isSignUp={isSignUp} onChangeIsSignUp={setIsSignUp} />
                </div>

                <DialogFooter className="relative z-[1] flex flex-row items-center !justify-center gap-2 rounded-b-lg border-t border-gray-dark-1100 px-6 py-4 dark:border-gray-dark-900 dark:bg-black">
                    <p className="text-sm text-gray-dark-2200">By continuing, you agree to IO's <a className="text-white underline" href="http://io.net/terms" target="_blank">Terms of Service</a> and <a className="text-white underline" href="http://io.net/privacy" target="_blank">Privacy Policy</a>.</p>
                    <div className="flex-1"></div>
                    {!cannotClose && <Button
                        onClick={onClose}
                        variant="outline"
                        size="sm"
                        className="min-w-[95px]"
                    >
                        Close
                    </Button>}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};